/**
 * Claim page — search, claim, and verify ownership all in one flow.
 */
import { esc, stars } from '../lib/helpers.js';

(() => {
  const input = document.getElementById('claim-search');
  const resultsEl = document.getElementById('claim-results');
  const hint = document.getElementById('claim-hint');
  const stepsEl = document.getElementById('claim-steps');
  const statusEl = document.getElementById('claim-status');
  if (!input || !resultsEl) return;

  const devToken = localStorage.getItem('ps_dev_token');
  const authOpts = devToken
    ? { credentials: 'include', headers: { Authorization: 'Bearer ' + devToken } }
    : { credentials: 'include' };

  const authFetch = (url, init = {}) => {
    const headers = { ...(authOpts.headers || {}), ...(init.headers || {}) };
    return fetch(url, { ...init, credentials: 'include', headers });
  };

  const trackClaim = (event, data = {}) => {
    const { place_id, claim_id, business_name, ...metadata } = data;
    const payload = {
      event,
      place_id,
      claim_id,
      business_name,
      metadata: { source: 'claim_page', ...metadata },
    };
    const headers = { 'Content-Type': 'application/json', ...(authOpts.headers || {}) };
    fetch('/api/claim-events', {
      method: 'POST',
      headers,
      credentials: 'include',
      keepalive: true,
      body: JSON.stringify(payload),
    }).catch(() => {});
    if (typeof window.gtag === 'function') window.gtag('event', event, payload.metadata);
  };

  const claimErrorCategory = (error) => {
    const msg = String(error || '').toLowerCase();
    if (msg.includes('already')) return 'already_claimed';
    if (msg.includes('too many')) return 'rate_limited';
    if (msg.includes('network')) return 'network';
    if (msg.includes('email')) return 'email_verification';
    if (msg.includes('code')) return 'phone_code';
    return 'unknown';
  };

  let debounceTimer = null;
  let lastQuery = '';
  let currentUser = null;

  // ── Step indicator ──
  const setStep = (n) => {
    if (!stepsEl) return;
    stepsEl.querySelectorAll('[data-step]').forEach(el => {
      const step = parseInt(el.dataset.step);
      const circle = el.querySelector('.step-circle');
      const label = el.querySelector('.step-label');
      if (step <= n) {
        circle.className = 'step-circle w-8 h-8 rounded-full bg-blue-600 text-white text-sm font-bold flex items-center justify-center';
        label.className = 'step-label text-sm font-medium text-slate-900';
      } else {
        circle.className = 'step-circle w-8 h-8 rounded-full bg-slate-200 text-slate-500 text-sm font-bold flex items-center justify-center';
        label.className = 'step-label text-sm text-slate-400';
      }
    });
  };

  // ── Auth check ──
  const checkAuth = () => {
    return authFetch('/auth/session/verify')
      .then(r => r.json())
      .then(d => { currentUser = d.ok ? d.user : null; return currentUser; })
      .catch(() => { currentUser = null; return null; });
  };

  // ── Show login prompt ──
  const showLoginPrompt = (bizName) => {
    statusEl.innerHTML = `
      <div class="max-w-lg mx-auto bg-blue-50 border border-blue-200 rounded-xl p-6 text-center">
        <p class="font-semibold text-slate-900 mb-2">Sign in to claim ${esc(bizName)}</p>
        <p class="text-sm text-slate-600 mb-4">You need an account to verify business ownership.</p>
        <div class="flex flex-col sm:flex-row gap-2 justify-center">
          <a href="/account/?return=${encodeURIComponent('/claim/')}" class="btn btn-primary btn-sm">Sign in with Google</a>
          <a href="/account/?return=${encodeURIComponent('/claim/')}" class="btn btn-ghost btn-sm">Sign in with email</a>
        </div>
      </div>`;
    statusEl.style.display = '';
  };

  // ── Claim a business ──
  const claimBusiness = (placeId, bizName, btn) => {
    btn.disabled = true;
    btn.textContent = 'Claiming...';

    authFetch('/auth/claims', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ place_id: placeId, business_name: bizName }),
    })
      .then(r => r.json())
      .then(res => {
        if (!res.ok) {
          trackClaim('claim_error', { place_id: placeId, business_name: bizName, category: claimErrorCategory(res.error), stage: 'create_claim' });
          if (res.error && res.error.includes('already')) {
            btn.textContent = 'Already claimed';
            btn.className = btn.className.replace('bg-blue-50', 'bg-slate-100').replace('text-blue-600', 'text-slate-500').replace('border-blue-200', 'border-slate-200');
          } else {
            btn.textContent = res.error || 'Failed';
            btn.className = btn.className.replace('bg-blue-50', 'bg-red-50').replace('text-blue-600', 'text-red-600').replace('border-blue-200', 'border-red-200');
            setTimeout(() => { btn.disabled = false; btn.textContent = 'Claim'; }, 3000);
          }
          return;
        }

        setStep(2);

        if (res.status === 'approved') {
          showApproved(bizName, placeId);
        } else if (res.domain || res.contact_email || res.phone) {
          showVerify(res.claim_id, res.domain, res.contact_email, res.phone, bizName, placeId);
        } else {
          showPending(bizName);
        }
      })
      .catch(() => {
        trackClaim('claim_error', { place_id: placeId, business_name: bizName, category: 'network', stage: 'create_claim' });
        btn.textContent = 'Error — retry';
        btn.disabled = false;
      });
  };

  // ── Auto-approved ──
  const showApproved = (bizName, placeId) => {
    setStep(3);
    statusEl.innerHTML = `
      <div class="max-w-lg mx-auto bg-green-50 border border-green-200 rounded-xl p-6 text-center">
        <svg class="w-10 h-10 text-green-600 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"/><path d="m9 12 2 2 4-4"/></svg>
        <p class="font-semibold text-green-800 text-lg mb-1">You're verified!</p>
        <p class="text-sm text-green-700 mb-4">Your email matched the business domain, so ownership of <strong>${esc(bizName)}</strong> was confirmed automatically.</p>
        <a href="/account/#/business/${encodeURIComponent(placeId || '')}" class="btn btn-success btn-sm">Set up your business profile</a>
      </div>`;
    statusEl.style.display = '';
    resultsEl.style.display = 'none';
    input.closest('section').querySelector('.relative')?.classList.add('hidden');
    if (hint) hint.style.display = 'none';
  };

  // ── Pending (no verification method available) ──
  const showPending = (bizName) => {
    statusEl.innerHTML = `
      <div class="max-w-lg mx-auto bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
        <p class="font-semibold text-amber-800 mb-1">Claim submitted</p>
        <p class="text-sm text-amber-700 mb-4">Your claim for <strong>${esc(bizName)}</strong> is pending admin review. We'll notify you by email once it's processed.</p>
        <a href="/account/" class="btn btn-ghost btn-sm">Go to your account</a>
      </div>`;
    statusEl.style.display = '';
    resultsEl.style.display = 'none';
  };

  // ── Verification flow (email or phone) ──
  const showVerify = (claimId, domain, contactEmail, phone, bizName, placeId) => {
    trackClaim('claim_verify_open', { claim_id: claimId, business_name: bizName, has_email: Boolean(domain || contactEmail), has_phone: Boolean(phone) });
    const hasEmail = domain || contactEmail;
    const hasPhone = !!phone;
    let mode = 'email';

    let emailPlaceholder = 'you@yourbusiness.com';
    let emailDesc = '';
    if (domain && contactEmail) {
      emailDesc = `Enter an email at <strong>@${esc(domain)}</strong> or the business contact email.`;
      emailPlaceholder = `you@${domain}`;
    } else if (domain) {
      emailDesc = `Enter an email at <strong>@${esc(domain)}</strong> to verify.`;
      emailPlaceholder = `you@${domain}`;
    } else if (contactEmail) {
      emailDesc = 'Enter the business contact email on file.';
    }

    const phoneDesc = `We'll send a code to the business phone <strong>${esc(phone || '')}</strong>.`;

    statusEl.innerHTML = `
      <div class="max-w-lg mx-auto bg-white border border-slate-200 rounded-xl p-6">
        <p class="font-semibold text-slate-900 mb-1 text-center">Verify you own ${esc(bizName)}</p>
        <p id="cv-desc" class="text-sm text-slate-600 mb-4 text-center">${mode === 'email' ? emailDesc : phoneDesc}</p>
        <form id="cv-form" class="space-y-3">
          <input id="cv-input" type="${mode === 'email' ? 'email' : 'hidden'}" placeholder="${esc(emailPlaceholder)}"
            class="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            ${mode === 'phone' ? 'style="display:none"' : ''} />
          <button id="cv-btn" type="submit" class="btn btn-primary btn-sm w-full">
            ${mode === 'email' ? 'Send verification email' : 'Send verification code'}
          </button>
        </form>
        <p id="cv-msg" class="text-sm mt-3 text-center hidden"></p>
        <p id="cv-toggle" class="mt-2 text-center ${hasEmail && hasPhone ? '' : 'hidden'}">
          <a href="#" class="text-xs text-blue-600 hover:underline">${mode === 'email' ? 'Verify by phone instead' : 'Verify by email instead'}</a>
        </p>
      </div>`;
    statusEl.style.display = '';
    resultsEl.style.display = 'none';
    input.closest('section').querySelector('.relative')?.classList.add('hidden');
    if (hint) hint.style.display = 'none';

    const cvInput = document.getElementById('cv-input');
    const cvBtn = document.getElementById('cv-btn');
    const cvMsg = document.getElementById('cv-msg');
    const cvDesc = document.getElementById('cv-desc');
    const cvToggle = document.getElementById('cv-toggle');

    // Toggle email/phone
    if (cvToggle) {
      cvToggle.querySelector('a').onclick = (e) => {
        e.preventDefault();
        mode = mode === 'email' ? 'phone' : 'email';
        cvMsg.classList.add('hidden');
        cvBtn.disabled = false;
        if (mode === 'email') {
          cvDesc.innerHTML = emailDesc;
          cvInput.type = 'email'; cvInput.style.display = ''; cvInput.value = ''; cvInput.placeholder = emailPlaceholder;
          cvBtn.textContent = 'Send verification email';
          cvToggle.querySelector('a').textContent = 'Verify by phone instead';
        } else {
          cvDesc.innerHTML = phoneDesc;
          cvInput.type = 'hidden'; cvInput.style.display = 'none'; cvInput.value = '';
          cvBtn.textContent = 'Send verification code';
          cvToggle.querySelector('a').textContent = 'Verify by email instead';
        }
      };
    }

    // Form submit
    document.getElementById('cv-form').onsubmit = (e) => {
      e.preventDefault();
      cvBtn.disabled = true;
      cvMsg.classList.add('hidden');

      if (mode === 'email') {
        const email = cvInput.value.trim();
        if (!email) { cvBtn.disabled = false; return; }
        trackClaim('claim_verify_email_submit', { claim_id: claimId, business_name: bizName });
        cvBtn.textContent = 'Sending...';
        authFetch(`/auth/claims/${claimId}/send-verification`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        })
          .then(r => r.json())
          .then(d => {
            cvMsg.classList.remove('hidden');
            if (d.ok) {
              trackClaim('claim_verification_email_requested', { claim_id: claimId, business_name: bizName });
              cvMsg.className = 'text-sm mt-3 text-center text-green-700';
              cvMsg.textContent = d.message || 'Verification email sent! Check your inbox.';
              cvBtn.textContent = 'Email sent — check your inbox';
            } else {
              trackClaim('claim_error', { claim_id: claimId, business_name: bizName, category: claimErrorCategory(d.error), stage: 'send_verification_email' });
              cvMsg.className = 'text-sm mt-3 text-center text-red-600';
              cvMsg.textContent = d.error || 'Failed to send.';
              cvBtn.disabled = false; cvBtn.textContent = 'Send verification email';
            }
          })
          .catch(() => {
            trackClaim('claim_error', { claim_id: claimId, business_name: bizName, category: 'network', stage: 'send_verification_email' });
            cvMsg.classList.remove('hidden'); cvMsg.className = 'text-sm mt-3 text-center text-red-600';
            cvMsg.textContent = 'Network error.'; cvBtn.disabled = false; cvBtn.textContent = 'Send verification email';
          });
      } else {
        // Phone: send code
        trackClaim('claim_phone_code_submit', { claim_id: claimId, business_name: bizName });
        cvBtn.textContent = 'Sending code...';
        authFetch(`/auth/claims/${claimId}/send-phone-code`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
        })
          .then(r => r.json())
          .then(d => {
            if (d.ok) {
              trackClaim('claim_phone_code_requested', { claim_id: claimId, business_name: bizName, method: d.method || null });
              cvMsg.classList.remove('hidden'); cvMsg.className = 'text-sm mt-3 text-center text-green-700';
              cvMsg.textContent = d.message || 'Code sent!';
              // Switch to code entry
              cvDesc.textContent = 'Enter the 6-digit code:';
              cvInput.type = 'text'; cvInput.style.display = ''; cvInput.value = '';
              cvInput.placeholder = '000000'; cvInput.maxLength = 6;
              cvBtn.disabled = false; cvBtn.textContent = 'Verify code';
              if (cvToggle) cvToggle.classList.add('hidden');

              document.getElementById('cv-form').onsubmit = (ev) => {
                ev.preventDefault();
                const code = cvInput.value.trim();
                if (!code || code.length !== 6) return;
                cvBtn.disabled = true; cvBtn.textContent = 'Verifying...';
                authFetch(`/auth/claims/${claimId}/verify-phone-code`, {
                  method: 'POST', headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ code }),
                })
                  .then(r => r.json())
                  .then(d2 => {
                    cvMsg.classList.remove('hidden');
                    if (d2.ok) {
                      trackClaim('claim_phone_verified', { claim_id: claimId, business_name: bizName });
                      cvMsg.className = 'text-sm mt-3 text-center text-green-700';
                      cvMsg.textContent = d2.message || 'Verified!';
                      setTimeout(() => showApproved(bizName, placeId), 1000);
                    } else {
                      trackClaim('claim_error', { claim_id: claimId, business_name: bizName, category: claimErrorCategory(d2.error), stage: 'verify_phone_code' });
                      cvMsg.className = 'text-sm mt-3 text-center text-red-600';
                      cvMsg.textContent = d2.error || 'Invalid code.';
                      cvBtn.disabled = false; cvBtn.textContent = 'Verify code';
                    }
                  })
                  .catch(() => {
                    trackClaim('claim_error', { claim_id: claimId, business_name: bizName, category: 'network', stage: 'verify_phone_code' });
                    cvMsg.classList.remove('hidden'); cvMsg.className = 'text-sm mt-3 text-center text-red-600';
                    cvMsg.textContent = 'Network error.'; cvBtn.disabled = false; cvBtn.textContent = 'Verify code';
                  });
              };
            } else {
              trackClaim('claim_error', { claim_id: claimId, business_name: bizName, category: claimErrorCategory(d.error), stage: 'send_phone_code' });
              cvMsg.classList.remove('hidden'); cvMsg.className = 'text-sm mt-3 text-center text-red-600';
              cvMsg.textContent = d.error || 'Failed.';
              cvBtn.disabled = false; cvBtn.textContent = 'Send verification code';
            }
          })
          .catch(() => {
            trackClaim('claim_error', { claim_id: claimId, business_name: bizName, category: 'network', stage: 'send_phone_code' });
            cvMsg.classList.remove('hidden'); cvMsg.className = 'text-sm mt-3 text-center text-red-600';
            cvMsg.textContent = 'Network error.'; cvBtn.disabled = false; cvBtn.textContent = 'Send verification code';
          });
      }
    };

    if (mode === 'email' && cvInput) cvInput.focus();
  };

  // ── Search ──
  const search = (q) => {
    if (q.length < 2) {
      resultsEl.innerHTML = '';
      if (hint) hint.style.display = '';
      return;
    }
    if (q === lastQuery) return;
    lastQuery = q;
    trackClaim('claim_search', { query_length: q.length });

    resultsEl.innerHTML = '<div class="text-center py-8"><span class="loading loading-spinner loading-md text-blue-600"></span></div>';
    if (hint) hint.style.display = 'none';
    statusEl.style.display = 'none';

    fetch(`/api?q=${encodeURIComponent(q)}&limit=12`)
      .then(r => r.json())
      .then(d => {
        if (!d.ok || !d.results || d.results.length === 0) {
          trackClaim('claim_search_results', { query_length: q.length, result_count: 0 });
          resultsEl.innerHTML = `
            <div class="text-center py-8">
              <p class="text-slate-500 text-sm mb-2">No businesses found for "${esc(q)}"</p>
              <p class="text-slate-400 text-xs">Try a different name or check the spelling.</p>
            </div>`;
          return;
        }

        trackClaim('claim_search_results', { query_length: q.length, result_count: d.results.length });
        let html = '';
        d.results.forEach(biz => {
          const logoHtml = biz.logo_url
            ? `<img src="${esc(biz.logo_url)}" alt="" class="w-10 h-10 rounded-lg object-cover bg-slate-100">`
            : `<div class="w-10 h-10 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-sm">${esc((biz.name || '?')[0])}</div>`;

          html += `<div class="flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-white">`;
          html += logoHtml;
          html += '<div class="flex-1 min-w-0">';
          html += `<p class="font-semibold text-slate-900 text-sm truncate">${esc(biz.name || 'Unknown')}</p>`;
          html += '<div class="flex items-center gap-2 text-xs text-slate-500 mt-0.5">';
          if (biz.city && biz.state) html += `<span>${esc(biz.city)}, ${esc(biz.state)}</span>`;
          if (biz.category) html += `<span class="text-slate-300">·</span><span>${esc(biz.category)}</span>`;
          html += '</div>';
          if (biz.rating) {
            html += `<div class="flex items-center gap-1.5 mt-1"><span class="text-amber-500 text-xs">${stars(biz.rating)}</span><span class="text-xs text-slate-400">${biz.rating}</span>`;
            if (biz.review_count) html += `<span class="text-xs text-slate-400">(${biz.review_count})</span>`;
            html += '</div>';
          }
          html += '</div>';
          html += `<button type="button" data-place-id="${esc(biz.place_id)}" data-biz-name="${esc(biz.name || '')}" class="claim-btn text-xs font-medium text-blue-600 shrink-0 px-3 py-1.5 rounded-lg border border-blue-200 bg-blue-50 hover:bg-blue-100 transition-colors cursor-pointer">Claim</button>`;
          html += '</div>';
        });

        resultsEl.innerHTML = html;

        // Attach claim handlers
        resultsEl.querySelectorAll('.claim-btn').forEach(btn => {
          btn.addEventListener('click', async () => {
            const placeId = btn.dataset.placeId;
            const bizName = btn.dataset.bizName;
            trackClaim('claim_click', { place_id: placeId, business_name: bizName });

            if (!currentUser) {
              // Check auth first
              const user = await checkAuth();
              if (!user) {
                trackClaim('claim_requires_login', { place_id: placeId, business_name: bizName });
                showLoginPrompt(bizName);
                return;
              }
            }

            claimBusiness(placeId, bizName, btn);
          });
        });
      })
      .catch(() => {
        trackClaim('claim_error', { category: 'network', stage: 'search', query_length: q.length });
        resultsEl.innerHTML = '<p class="text-center text-slate-400 text-sm py-8">Search unavailable. Please try again.</p>';
      });
  };

  input.addEventListener('input', () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => search(input.value.trim()), 300);
  });

  // Check auth on load (silent)
  trackClaim('claim_page_view');
  checkAuth();
  input.focus();
})();
